import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    useDisclosure,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { colors } from "../../styles";

interface ConfirmAlertProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    message: string;
    title?: string;
    confirmLabel?: string;
    isLoading?: boolean;
}

interface ConfirmOptions {
    message: string;
    title?: string;
    confirmLabel?: string;
    onConfirm: () => void;
    /** false면 확인 클릭 시 닫지 않음 (성공 후 호출측에서 닫기). 기본 true */
    closeOnConfirm?: boolean;
}

export function useConfirmAlert() {

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [options, setOptions] = useState<ConfirmOptions | null>(null);

    const showConfirm = (nextOptions: ConfirmOptions) => {
        setOptions(nextOptions);
        onOpen();
    };

    const handleClose = () => {
        onClose();
        setOptions(null);
    };

    const handleConfirm = () => {
        options?.onConfirm();
        if (options?.closeOnConfirm !== false) {
            handleClose();
        }
    };

    return {
        showConfirm,
        confirmAlertProps: {
            isOpen,
            onClose: handleClose,
            onConfirm: handleConfirm,
            message: options?.message ?? "",
            title: options?.title,
            confirmLabel: options?.confirmLabel,
        },
    };
}

export default function ConfirmAlert({
    isOpen,
    onClose,
    onConfirm,
    message,
    title,
    confirmLabel = "확인",
    isLoading = false,
}: ConfirmAlertProps) {
    const cancelRef = useRef<HTMLButtonElement>(null);

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={isLoading ? () => undefined : onClose}
        >
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize={18} fontWeight={700}>
                        {title}
                    </AlertDialogHeader>
                    <AlertDialogBody fontSize={16} fontWeight={400}>
                        {message}
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
                            취소
                        </Button>
                        <Button
                            backgroundColor={colors.black}
                            color={colors.white}
                            onClick={onConfirm}
                            ml={3}
                            isLoading={isLoading}
                        >
                            {confirmLabel}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
}
